import { api } from "./api";

const DOSSIER_KEY = "vault.bookmarks.v1";
const DEEPDIVE_KEY = "vault.deepdive.saved.v1";
const CONNECTION_KEY = "vault.connection.saved.v1";
const TOKEN_KEY = "vault.auth.token";

// Generous cap per list. At ~36 bytes per entry, 1000 entries ≈ 36KB.
// localStorage typically allows 5–10MB per origin, so all 3 lists fit ~150x over.
const MAX_PER_LIST = 1000;

const isAuthed = () => {
  try {
    return !!localStorage.getItem(TOKEN_KEY);
  } catch {
    return false;
  }
};

// Fire-and-forget push to cloud when authenticated. Silently no-ops for anon users.
// Also nudges the sigil engine so foia_hound / curator can fire as soon as you cross
// the milestone (without waiting for a profile-page refresh).
const pushToggle = (type, targetId) => {
  if (!isAuthed()) return;
  api.post("/me/bookmarks/toggle", { type, target_id: targetId })
    .then(() => {
      // Lazy-import so anon flows stay free of confetti / sonner dependencies
      import("./sigilCelebration").then((m) => m.celebrateNewSigils().catch(() => {})).catch(() => {});
    })
    .catch(() => {});
};

const read = (k) => {
  try {
    const v = JSON.parse(localStorage.getItem(k) || "[]");
    return Array.isArray(v) ? v : [];
  } catch {
    return [];
  }
};

const write = (k, v) => {
  try {
    // Cap: when over MAX_PER_LIST, drop the oldest (front of array) so the
    // newest saves always stick. This keeps the list bounded forever.
    const list = v.length > MAX_PER_LIST ? v.slice(v.length - MAX_PER_LIST) : v;
    localStorage.setItem(k, JSON.stringify(list));
    return true;
  } catch (e) {
    // QuotaExceededError — try once more after pruning to half the cap
    try {
      const pruned = v.slice(Math.max(0, v.length - Math.floor(MAX_PER_LIST / 2)));
      localStorage.setItem(k, JSON.stringify(pruned));
      return true;
    } catch {
      return false;
    }
  }
};

// ----- Dossier bookmarks -----
export const getBookmarks = () => read(DOSSIER_KEY);
export const isBookmarked = (slug) => getBookmarks().includes(slug);
export const toggleBookmark = (slug) => {
  const list = getBookmarks();
  const next = list.includes(slug) ? list.filter((s) => s !== slug) : [...list, slug];
  write(DOSSIER_KEY, next);
  pushToggle("dossier", slug);
  window.dispatchEvent(new Event("bookmarks-changed"));
  return next.includes(slug);
};
export const setBookmarks = (list) => {
  write(DOSSIER_KEY, list);
  window.dispatchEvent(new Event("bookmarks-changed"));
};

// ----- Saved AI deep-dives -----
export const getSavedDeepDives = () => read(DEEPDIVE_KEY);
export const isDeepDiveSaved = (id) => getSavedDeepDives().includes(id);
export const toggleDeepDiveSaved = (id) => {
  const list = getSavedDeepDives();
  const wasIn = list.includes(id);
  const next = wasIn ? list.filter((s) => s !== id) : [...list, id];
  write(DEEPDIVE_KEY, next);
  pushToggle("ai_dossier", id);
  window.dispatchEvent(new Event("bookmarks-changed"));
  return next.includes(id);
};
export const setSavedDeepDives = (list) => {
  write(DEEPDIVE_KEY, list);
  window.dispatchEvent(new Event("bookmarks-changed"));
};

// ----- Saved Connections -----
export const getSavedConnections = () => read(CONNECTION_KEY);
export const isConnectionSaved = (id) => getSavedConnections().includes(id);
export const toggleConnectionSaved = (id) => {
  const list = getSavedConnections();
  const wasIn = list.includes(id);
  const next = wasIn ? list.filter((s) => s !== id) : [...list, id];
  write(CONNECTION_KEY, next);
  pushToggle("connection", id);
  window.dispatchEvent(new Event("bookmarks-changed"));
  return next.includes(id);
};
export const setSavedConnections = (list) => {
  write(CONNECTION_KEY, list);
  window.dispatchEvent(new Event("bookmarks-changed"));
};

export const clearAllSaved = () => {
  write(DOSSIER_KEY, []);
  write(DEEPDIVE_KEY, []);
  write(CONNECTION_KEY, []);
  // If signed-in, also clear the cloud copy so the next device sees an empty library too.
  if (isAuthed()) {
    api.delete("/me/bookmarks").catch(() => {});
  }
  window.dispatchEvent(new Event("bookmarks-changed"));
};

// ----- Combined count for navbar -----
export const getTotalSavedCount = () =>
  getBookmarks().length + getSavedDeepDives().length + getSavedConnections().length;

// ----- Storage usage report (used by BookmarksPage to reassure users) -----
export const getStorageReport = () => {
  const sizes = {
    dossiers: (localStorage.getItem(DOSSIER_KEY) || "").length,
    deepdives: (localStorage.getItem(DEEPDIVE_KEY) || "").length,
    connections: (localStorage.getItem(CONNECTION_KEY) || "").length,
  };
  const used = sizes.dossiers + sizes.deepdives + sizes.connections;
  // localStorage browser quota is typically 5MB; we cap our own use to MAX_PER_LIST entries per list.
  return {
    bytes_used: used,
    bytes_cap: MAX_PER_LIST * 3 * 40, // approx upper bound for slugs/uuids
    max_per_list: MAX_PER_LIST,
  };
};
